import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { Toaster, toast } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ObjectivesPage from "./pages/ObjectivesMatrixPage";
import ReworkQueuePage from "./pages/ReworkQueuePage";
import Sales from "./pages/Sales";
import SaleDetail from "./pages/SaleDetail";
import SaleForm from "./pages/SaleForm";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import ClientForm from "./pages/ClientForm";
import Agencies from "./pages/Agencies";
import Products from "./pages/Products";
import ImportPage from "./pages/ImportPage";
import Export from "./pages/Export";
import Integrations from "./pages/Integrations";
import CustomFields from "./pages/CustomFields";
import Users from "./pages/Users";
import EditRequests from "./pages/EditRequests";
import AuditLogs from "./pages/AuditLogs";
import Reconciliation from "./pages/Reconciliation";
import FuzzyReconciliation from "./pages/FuzzyReconciliation";
import Agents from "./pages/Agents";
import AgentMappings from "./pages/AgentMappings";
import SFIntegration from "./pages/SFIntegration";
// Iter52 P14l — GDPR Compliance page hidden from UI (DB data preserved).
// import GdprCompliance from "./pages/GdprCompliance";
import AdminCommercialMismatches from "./pages/AdminCommercialMismatches";
import AdminDictionaries from "./pages/AdminDictionaries";
import AdminClassificationRules from "./pages/AdminClassificationRules";
import AdminFieldAccess from "./pages/AdminFieldAccess";
import AdminFlacSecurity from "./pages/AdminFlacSecurity";
import AdminCleanup from "./pages/AdminCleanup";
import ProposalsAdmin from "./pages/ProposalsAdmin";
import FieldParityReport from "./pages/FieldParityReport";
import DataReconciliation from "./pages/DataReconciliation";
import EnergySuppliersAdmin from "./pages/EnergySuppliersAdmin";
import Documentation from "./pages/Documentation";
import Layout from "./components/Layout";
import "./App.css";

// Redirect /contracts/:id → /sales/:id (interpolează efectiv param-ul; backend-ul acceptă și contract_id)
const ContractIdRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/sales/${id}`} replace />;
};


const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const denied = !loading && user && allowedRoles && !allowedRoles.includes(user.role);
  
  useEffect(() => {
    if (denied) {
      toast.error("Nu ai permisiuni pentru această zonă.", {
        id: "rbac-denied", // dedupe — same toast nu se reafișează la fiecare re-render
        duration: 3500,
      });
    }
  }, [denied]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (denied) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="objectives" element={<ObjectivesPage />} />
        <Route path="rework" element={<ReworkQueuePage />} />
        
        {/* Sales (previously Contracts) */}
        <Route path="sales" element={<Sales />} />
        <Route path="sales/new" element={<SaleForm />} />
        <Route path="sales/:id" element={<SaleDetail />} />
        <Route path="sales/:id/edit" element={<SaleForm />} />
        
        {/* Clients */}
        <Route path="clients" element={<Clients />} />
        <Route path="clients/new" element={<ClientForm />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="clients/:id/edit" element={<ClientForm />} />
        
        {/* Legacy routes redirect to new ones */}
        <Route path="contracts" element={<Navigate to="/sales" replace />} />
        <Route path="contracts/new" element={<Navigate to="/sales/new" replace />} />
        <Route path="contracts/:id" element={<ContractIdRedirect />} />
        
        {/* Admin routes */}
        <Route path="agencies" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Agencies />
          </ProtectedRoute>
        } />
        <Route path="products" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Products />
          </ProtectedRoute>
        } />
        <Route path="users" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Users />
          </ProtectedRoute>
        } />
        <Route path="custom-fields" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CustomFields />
          </ProtectedRoute>
        } />
        <Route path="integrations" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Integrations />
          </ProtectedRoute>
        } />
        
        {/* Edit Requests */}
        <Route path="edit-requests" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <EditRequests />
          </ProtectedRoute>
        } />
        
        {/* Import/Export */}
        <Route path="import" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <ImportPage />
          </ProtectedRoute>
        } />
        <Route path="export" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Export />
          </ProtectedRoute>
        } />
        
        {/* Audit Logs */}
        <Route path="audit-logs" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <AuditLogs />
          </ProtectedRoute>
        } />
        
        {/* Reconciliation (P0-3) */}
        <Route path="reconciliation" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice", "manager"]}>
            <Reconciliation />
          </ProtectedRoute>
        } />
        <Route path="reconciliation/fuzzy" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <FuzzyReconciliation />
          </ProtectedRoute>
        } />
        
        {/* Agents (commercial identity — BATCH 3.4) */}
        <Route path="agents" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice", "manager"]}>
            <Agents />
          </ProtectedRoute>
        } />
        
        {/* Agent Mappings (aliases per source — BATCH 3.5) */}
        <Route path="agent-mappings" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice", "manager"]}>
            <AgentMappings />
          </ProtectedRoute>
        } />

        {/* Iter25 — SF Live Integration admin (webhook + DLQ) */}
        <Route path="admin/sf-integration" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <SFIntegration />
          </ProtectedRoute>
        } />

        {/* Iter52 P14l — GDPR Compliance page removed from UI.
            Data still kept in DB; consent flags shown read-only on Client details page.
            Route preserved (commented) so it can be reactivated if needed without re-implementing. */}
        {/* <Route path="admin/gdpr-compliance" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <GdprCompliance />
          </ProtectedRoute>
        } /> */}
        
        {/* Iter32 P4 — Commercial Ownership Mismatches admin report */}
        <Route path="admin/commercial-ownership-mismatches" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <AdminCommercialMismatches />
          </ProtectedRoute>
        } />
        
        {/* Iter33 P5 — Admin Dictionaries (offer_type_mappings) */}
        <Route path="admin/dictionaries" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <AdminDictionaries />
          </ProtectedRoute>
        } />

        {/* Iter52 P9 — Admin Classification Rules (product_classification_rules) */}
        <Route path="admin/classification-rules" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <AdminClassificationRules />
          </ProtectedRoute>
        } />

        {/* Iter38 — Field Access Matrix (FLAC) */}
        <Route path="admin/field-access" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <AdminFieldAccess />
          </ProtectedRoute>
        } />

        {/* Iter38b — FLAC Security Dashboard (denials analytics) */}
        <Route path="admin/flac-security" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice"]}>
            <AdminFlacSecurity />
          </ProtectedRoute>
        } />

        {/* Iter51 E29c — Admin Cleanup Test Data */}
        <Route path="admin/cleanup" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminCleanup />
          </ProtectedRoute>
        } />

        {/* Iter51 Etapa 3 — Offer/Agent Proposals review queue */}
        <Route path="admin/proposals" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ProposalsAdmin />
          </ProtectedRoute>
        } />

        {/* Iter64d — Reconciliere date istorice (preview + aplicare per rând) */}
        <Route path="admin/data-reconciliation" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <DataReconciliation />
          </ProtectedRoute>
        } />

        {/* Iter51 Etapa 8 — Field Parity Report dashboard */}
        <Route path="admin/field-parity-report" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <FieldParityReport />
          </ProtectedRoute>
        } />

        {/* Iter51 Etapa 11 — Energy Suppliers CRUD */}
        <Route path="admin/energy-suppliers" element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <EnergySuppliersAdmin />
          </ProtectedRoute>
        } />

        {/* Documentation (PDF guides) — visible to admin, backoffice, manager */}
        <Route path="documentation" element={
          <ProtectedRoute allowedRoles={["admin", "backoffice", "manager"]}>
            <Documentation />
          </ProtectedRoute>
        } />
      </Route>
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
